import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DownloadPersonsDataRequest, SearchService } from 'ldt-people-api';
import { QuickBuild, QuickBuildService } from '../people/quick-build.service';
import { AuthService } from '../auth/service/auth.service';
import {
  FileType,
  ListAllReportDeliveriesRequest,
  ReportDelivery,
  ReportsService,
} from 'ldt-data-deliveries-api';
import { Log } from '../logger.service';
import { IServerSideGetRowsParams } from 'ag-grid-community';
import { NotificationService } from '../shared/notification-service/notification.service';
import * as dayjs from 'dayjs';
import { MatDialog } from '@angular/material/dialog';
import {
  FileSelectionDialogComponent,
  FileInfo,
} from '../shared/components/file-selection-dialog/file-selection-dialog.component';

export enum DownloadType {
  MOST_RECENT_JOBS_CSV = 'most-recent-jobs-csv',
  FULL_CSV = 'full-csv',
  JSONL = 'jsonl',
  CUSTOM_CSV = 'custom-csv',
}

export interface DownloadFormat {
  value: DownloadType;
  label: 'Most Recent Jobs CSV' | 'Full CSV' | 'JSONL' | 'Custom CSV';
  description: string;
  fileTypeDescriptor: 'CSV' | 'JSONL';
  requestParams?: {
    return_fields?: string[];
    past_jobs?: number;
  };
  featureDescriptions: {
    key: string;
    description: string;
    indicator: string;
  }[];
  sampleLink?: string;
}

export const DOWNLOAD_FORMATS: DownloadFormat[] = [
  {
    value: DownloadType.MOST_RECENT_JOBS_CSV,
    label: 'Most Recent Jobs CSV',
    fileTypeDescriptor: 'CSV',
    description:
      'A curated list of the most relevant fields for the person and their most recent job history that is easily usable in tools like Excel for analysis.',
    requestParams: {
      return_fields: [
        'id',
        'name',
        'linkedin',
        'employment_status',
        'jobs.company.name',
        'jobs.title',
        'jobs.level',
        'jobs.function',
        'jobs.started_at',
        'jobs.ended_at',
        'first_name',
        'last_name',
        'connections',
        'location',
        'location_details.msa',
        'location_details.region',
        'location_details.country',
        'jobs.email_status',
        'jobs.email',
        'jobs.company_tenure',
        'jobs.company.linkedin',
        'jobs.company.ticker',
        'jobs.company.domain',
        'jobs.company.ultimate_parent.ticker',
        'jobs.company.ultimate_parent.domain',
        'jobs.company.ultimate_parent.linkedin',
        'jobs.company.ultimate_parent.name',
        'info_change_detected_at',
        'title_change_detected_at',
        'company_change_detected_at',
      ],
      past_jobs: 3,
    },
    featureDescriptions: [
      {
        key: 'personData',
        description: 'All fields',
        indicator: 'full',
      },
      {
        key: 'jobHistory',
        description: 'Last 3 jobs',
        indicator: 'partial',
      },
      {
        key: 'jobDetails',
        description: 'Key details',
        indicator: 'partial',
      },
      {
        key: 'companyData',
        description: 'Identifying details',
        indicator: 'partial',
      },
      {
        key: 'educationData',
        description: 'N/A',
        indicator: 'none',
      },
      {
        key: 'fileFormat',
        description: 'CSV',
        indicator: 'full',
      },
      {
        key: 'columnCount',
        description: '~60 columns',
        indicator: 'full',
      },
      {
        key: 'excelCompatible',
        description: 'Yes',
        indicator: 'full',
      },
      {
        key: 'machineReadable',
        description: 'Yes',
        indicator: 'full',
      },
      {
        key: 'bestFor',
        description: 'Most use cases',
        indicator: 'full',
      },
    ],
    sampleLink:
      'https://docs.google.com/spreadsheets/d/1y1Qyel60FGV1nmIJpPqUIlsc91bOGN8EN8wMbqNM9Eg/edit?usp=sharing',
  },
  {
    value: DownloadType.FULL_CSV,
    label: 'Full CSV',
    fileTypeDescriptor: 'CSV',
    description:
      'All the fields available for the person, their job history, education details, and more. This format may be difficult to use in tools like Excel.',
    featureDescriptions: [
      {
        key: 'personData',
        description: 'All fields',
        indicator: 'full',
      },
      {
        key: 'jobHistory',
        description: 'All historical',
        indicator: 'full',
      },
      {
        key: 'jobDetails',
        description: 'Full details',
        indicator: 'full',
      },
      {
        key: 'companyData',
        description: 'Full details',
        indicator: 'full',
      },
      {
        key: 'educationData',
        description: 'Full details',
        indicator: 'full',
      },
      {
        key: 'fileFormat',
        description: 'CSV',
        indicator: 'full',
      },
      {
        key: 'columnCount',
        description: '1000+ columns',
        indicator: 'full',
      },
      {
        key: 'excelCompatible',
        description: 'Row count dependent',
        indicator: 'partial',
      },
      {
        key: 'machineReadable',
        description: 'Yes',
        indicator: 'full',
      },
      {
        key: 'bestFor',
        description: 'Programmatic analysis',
        indicator: 'full',
      },
    ],
    sampleLink:
      'https://docs.google.com/spreadsheets/d/1y1Qyel60FGV1nmIJpPqUIlsc91bOGN8EN8wMbqNM9Eg/edit?gid=0#gid=0',
  },
  {
    value: DownloadType.JSONL,
    label: 'JSONL',
    fileTypeDescriptor: 'JSONL',
    description:
      'The full person record in gzipped JSONL format, perfect for machine analysis and programmatic use.',
    featureDescriptions: [
      {
        key: 'personData',
        description: 'All fields',
        indicator: 'full',
      },
      {
        key: 'jobHistory',
        description: 'All historical',
        indicator: 'full',
      },
      {
        key: 'jobDetails',
        description: 'Full details',
        indicator: 'full',
      },
      {
        key: 'companyData',
        description: 'Full details',
        indicator: 'full',
      },
      {
        key: 'educationData',
        description: 'Full details',
        indicator: 'full',
      },
      {
        key: 'fileFormat',
        description: 'jsonl.gz',
        indicator: 'full',
      },
      {
        key: 'columnCount',
        description: '1000+ fields',
        indicator: 'full',
      },
      {
        key: 'excelCompatible',
        description: 'No',
        indicator: 'none',
      },
      {
        key: 'machineReadable',
        description: 'Yes',
        indicator: 'full',
      },
      {
        key: 'bestFor',
        description: 'Programmatic analysis',
        indicator: 'full',
      },
    ],
    sampleLink:
      'https://drive.google.com/file/d/1nCndkOt87ORzysbx8lL-690IVnAUUZyp/view?usp=sharing',
  },
];

@Injectable({
  providedIn: 'root',
})
export class DownloadsService {
  constructor(
    private searchService: SearchService,
    private authService: AuthService,
    private reportService: ReportsService,
    private notify: NotificationService,
    private http: HttpClient,
    private dialog: MatDialog
  ) {}

  getDownload(deliveryId: number): Observable<ReportDelivery> {
    return this.reportService.getReportDelivery(this.authService.getSelectedOrgIdValue, deliveryId);
  }

  addDownload(
    quickBuild: QuickBuild,
    format: DownloadType,
    size: number | undefined = undefined,
    customRequestParams?: { return_fields?: string[]; past_jobs?: number },
    suggestedFilename?: string
  ): Observable<number> {
    // Build out the request using the passed in params and the set params for the format
    const [filters, _] = QuickBuildService.rootFilterGroupToAPIFilters(quickBuild.rootFilterGroup);
    const formatParams =
      customRequestParams || DOWNLOAD_FORMATS.find((f) => f.value === format)?.requestParams;
    let req: DownloadPersonsDataRequest = {
      filters: [filters],
      max_size: size,
      ...formatParams,
      name: suggestedFilename || dayjs().format('YYYY-MM-DD HH:mm') + ' Live Data Person Export',
      file_type: format === DownloadType.JSONL ? FileType.JsonlGz : FileType.Csv,
    };

    return this.searchService.downloadPersonsData(this.authService.getSelectedOrgIdValue, req).pipe(
      map((resp) => {
        Log.d('DownloadsService addDownload response:', resp, resp.report_delivery_id);
        if (!resp.report_delivery_id) {
          throw new Error('No report_delivery_id received from downloadPersonsData');
        }
        return resp.report_delivery_id;
      }),
      catchError((error) => {
        Log.e('Error in addDownload:', error);
        throw error;
      })
    );
  }

  public downloadFile(delivery: ReportDelivery): void {
    this.reportService
      .getReportDeliveryDownloadUrls(this.authService.getSelectedOrgIdValue, delivery.id.toString())
      .subscribe({
        next: (download) => {
          if (!download.urls || download.urls.length === 0) {
            this.notify.warning('No files available for download');
            return;
          }

          // Single file - download immediately
          if (download.urls.length === 1) {
            this.downloadSingleFile(download.urls[0]);
            return;
          }

          // Multiple files - show selection dialog
          this.showFileSelectionDialog(download.urls);
        },
        error: (error) => {
          this.notify.error('Failed to load downloads');
        },
      });
  }

  private showFileSelectionDialog(urls: string[]): void {
    // Extract filenames for display
    const files: FileInfo[] = urls.map((url, index) => ({
      url,
      filename: url.split('/').pop()?.split('?')[0] || `File ${index + 1}`,
      selected: true, // Default to all selected
    }));

    const dialogRef = this.dialog.open(FileSelectionDialogComponent, {
      data: { files },
      width: '600px',
      maxHeight: '80vh',
    });

    dialogRef.afterClosed().subscribe((selectedFiles: FileInfo[]) => {
      if (selectedFiles && selectedFiles.length > 0) {
        Log.d('DownloadsService: downloading', selectedFiles.length, 'files');

        // Show notification for multiple files
        if (selectedFiles.length > 1) {
          this.notify.info(`Downloading ${selectedFiles.length} files...`);
        }

        // Download each file with a delay to avoid browser issues
        selectedFiles.forEach((file, index) => {
          setTimeout(() => {
            this.downloadSingleFile(file.url);
          }, index * 500); // 500ms delay between downloads
        });
      }
    });
  }

  private downloadSingleFile(url: string): void {
    const filename = url.split('/').pop()?.split('?')[0];
    Log.d('DownloadsService: downloadSingleFile:', url, filename);

    // Trigger the browser to download the file
    this.http.get(url, { responseType: 'blob' }).subscribe((blob) => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename!;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    });
  }

  agGridDatasource() {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        params.api.hideOverlay();
        const size = (params.request.endRow || 0) - (params.request.startRow || 0);

        // If the grid is back at the top, reset the pagination token
        if (params.request.startRow === 0) {
          if (params.context) {
            params.context.paginationToken = undefined;
          } else {
            params.context = {
              paginationToken: undefined,
            };
          }
        }

        var sortBy = undefined;
        if (params.request.sortModel.length > 0) {
          sortBy = params.request.sortModel[0].colId;
          if (params.request.sortModel[0].sort == 'desc') {
            sortBy = '-' + sortBy;
          }
        }

        let searchRequest: ListAllReportDeliveriesRequest = {
          size: size,
          pagination_token: params.context?.paginationToken,
          sort_by: sortBy,
        };

        this.reportService
          .listReportDeliveries(this.authService.getSelectedOrgIdValue, searchRequest)
          .subscribe({
            next: (res) => {
              Log.d('DownloadsService agGridDatasource next:', res);
              params.context.paginationToken = res.pagination_token;
              params.success({
                rowData: res.items || [],
              });
              // Emit store refreshed event
              params.api.dispatchEvent({ type: 'storeRefreshed' });
            },
            error: () => {
              params.fail();
              this.notify.error('Oops. There was an error getting the data.');
            },
          });
      },
    };
  }
}
